/**
 * Query and mutation hooks.
 *
 * Every mutation invalidates the same broad set of keys. A finer-grained
 * scheme would be measurably faster and reliably wrong: a single transfer
 * moves balances, the dashboard, both funds' histories, the loan list and the
 * reports. Guessing which of those a given write touched is exactly how a
 * money UI ends up showing a stale balance.
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
  useInfiniteQuery,
  type UseQueryOptions,
} from '@tanstack/react-query';
import { api } from '@/lib/api';
import type {
  Account,
  AppSettings,
  BalancesResponse,
  CashflowReport,
  Category,
  CategoryTotal,
  Counterparty,
  Currency,
  DashboardResponse,
  Fund,
  FxRate,
  Loan,
  Location,
  Paged,
  Transaction,
  BalancePoint,
  CurrencyExposure,
  Commitment,
  Instalment,
  Forecast,
  UpcomingResponse,
  CalendarResponse,
} from '@/lib/types';

export const qk = {
  dashboard: ['dashboard'] as const,
  balances: (params?: unknown) => ['balances', params ?? null] as const,
  funds: ['funds'] as const,
  fund: (id: number) => ['funds', id] as const,
  locations: ['locations'] as const,
  accounts: (params?: unknown) => ['accounts', params ?? null] as const,
  categories: (params?: unknown) => ['categories', params ?? null] as const,
  counterparties: (q?: string) => ['counterparties', q ?? ''] as const,
  currencies: ['currencies'] as const,
  fxRates: ['fx', 'rates'] as const,
  transactions: (params?: unknown) => ['transactions', params ?? null] as const,
  transaction: (id: number) => ['transactions', id] as const,
  loans: (params?: unknown) => ['loans', params ?? null] as const,
  loan: (id: number) => ['loans', id] as const,
  reports: (name: string, params?: unknown) => ['reports', name, params ?? null] as const,
  settings: ['settings'] as const,
};

/** Everything a money write can plausibly affect. */
const LEDGER_KEYS = ['dashboard', 'balances', 'transactions', 'loans', 'reports', 'accounts', 'funds'];

function useLedgerInvalidation() {
  const qc = useQueryClient();
  return () => {
    for (const key of LEDGER_KEYS) void qc.invalidateQueries({ queryKey: [key] });
  };
}

// ───────────────────────────── reads ─────────────────────────────

export function useDashboard() {
  return useQuery({
    queryKey: qk.dashboard,
    queryFn: async () => (await api.get<DashboardResponse>('/dashboard')).data,
  });
}

export function useBalances(params?: { asOf?: string; fundId?: number; includeZero?: boolean }) {
  return useQuery({
    queryKey: qk.balances(params),
    queryFn: async () => (await api.get<BalancesResponse>('/balances', { params })).data,
  });
}

export function useFunds(includeArchived = false) {
  return useQuery({
    queryKey: [...qk.funds, includeArchived],
    queryFn: async () =>
      (await api.get<{ items: Fund[] }>('/funds', { params: { includeArchived } })).data.items,
    // Reference data — refetching it on every window focus is pure noise.
    staleTime: 5 * 60_000,
  });
}

export function useLocations(includeArchived = false) {
  return useQuery({
    queryKey: [...qk.locations, includeArchived],
    queryFn: async () =>
      (await api.get<{ items: Location[] }>('/locations', { params: { includeArchived } })).data.items,
    staleTime: 5 * 60_000,
  });
}

export function useAccounts(params?: { fundId?: number; locationId?: number }) {
  return useQuery({
    queryKey: qk.accounts(params),
    queryFn: async () => (await api.get<{ items: Account[] }>('/accounts', { params })).data.items,
    staleTime: 60_000,
  });
}

export function useCategories(params?: { kind?: 'INCOME' | 'EXPENSE'; fundId?: number }) {
  return useQuery({
    queryKey: qk.categories(params),
    queryFn: async () => (await api.get<{ items: Category[] }>('/categories', { params })).data.items,
    staleTime: 60_000,
  });
}

export function useCounterparties(q?: string) {
  return useQuery({
    queryKey: qk.counterparties(q),
    queryFn: async () =>
      (await api.get<{ items: Counterparty[] }>('/counterparties', { params: { q } })).data.items,
    staleTime: 60_000,
  });
}

export function useCurrencies() {
  return useQuery({
    queryKey: qk.currencies,
    queryFn: async () => (await api.get<{ items: Currency[] }>('/currencies')).data.items,
    staleTime: 30 * 60_000,
  });
}

export function useFxRates() {
  return useQuery({
    queryKey: qk.fxRates,
    queryFn: async () =>
      (await api.get<{ base: string; rates: FxRate[] }>('/fx/rates')).data,
    staleTime: 10 * 60_000,
  });
}

export interface TransactionFilters {
  from?: string;
  to?: string;
  fundId?: number;
  locationId?: number;
  accountId?: number;
  categoryId?: number;
  counterpartyId?: number;
  kind?: string;
  currency?: string;
  q?: string;
  includeVoided?: boolean;
  limit?: number;
}

export function useTransactions(filters: TransactionFilters = {}) {
  return useInfiniteQuery({
    queryKey: qk.transactions(filters),
    initialPageParam: undefined as number | undefined,
    queryFn: async ({ pageParam }) =>
      (
        await api.get<Paged<Transaction>>('/transactions', {
          params: { ...filters, cursor: pageParam },
        })
      ).data,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });
}

export function useTransaction(id: number | null) {
  return useQuery({
    queryKey: qk.transaction(id ?? 0),
    queryFn: async () => (await api.get<Transaction>(`/transactions/${id}`)).data,
    enabled: id != null,
  });
}

export function useLoans(params?: { includeClosed?: boolean; fundId?: number }) {
  return useQuery({
    queryKey: qk.loans(params),
    queryFn: async () => (await api.get<{ items: Loan[] }>('/loans', { params })).data.items,
  });
}

export function useCashflow(params: { from?: string; to?: string; fundId?: number }) {
  return useQuery({
    queryKey: qk.reports('cashflow', params),
    queryFn: async () => (await api.get<CashflowReport>('/reports/cashflow', { params })).data,
  });
}

export function useByCategory(params: {
  from?: string;
  to?: string;
  fundId?: number;
  kind?: 'INCOME' | 'EXPENSE';
}) {
  return useQuery({
    queryKey: qk.reports('by-category', params),
    queryFn: async () =>
      (
        await api.get<{ baseCurrency: string; baseDecimals: number; rows: CategoryTotal[] }>(
          '/reports/by-category',
          { params },
        )
      ).data,
  });
}

export function useBalanceHistory(params: { months?: number; fundId?: number }) {
  return useQuery({
    queryKey: qk.reports('balance-history', params),
    queryFn: async () =>
      (
        await api.get<{ baseCurrency: string; baseDecimals: number; points: BalancePoint[] }>(
          '/reports/balance-history',
          { params },
        )
      ).data,
  });
}

export function useFxExposure() {
  return useQuery({
    queryKey: qk.reports('fx-exposure'),
    queryFn: async () =>
      (
        await api.get<{
          baseCurrency: string;
          baseDecimals: number;
          asOf: string;
          rows: CurrencyExposure[];
        }>('/reports/fx-exposure')
      ).data,
  });
}

export function useSettings(options?: Partial<UseQueryOptions<AppSettings>>) {
  return useQuery({
    queryKey: qk.settings,
    queryFn: async () => (await api.get<AppSettings>('/settings')).data,
    staleTime: 5 * 60_000,
    ...options,
  });
}

// ───────────────────────────── writes ─────────────────────────────

export function useCreateEntry() {
  const invalidate = useLedgerInvalidation();
  return useMutation({
    mutationFn: async (body: Record<string, unknown>) =>
      (await api.post<Transaction>('/transactions', body)).data,
    onSuccess: invalidate,
  });
}

export function useCreateTransfer() {
  const invalidate = useLedgerInvalidation();
  return useMutation({
    mutationFn: async (body: Record<string, unknown>) =>
      (await api.post<Transaction>('/transfers', body)).data,
    onSuccess: invalidate,
  });
}

export function useCreateExchange() {
  const invalidate = useLedgerInvalidation();
  return useMutation({
    mutationFn: async (body: Record<string, unknown>) =>
      (await api.post<Transaction>('/exchanges', body)).data,
    onSuccess: invalidate,
  });
}

export function useVoidTransaction() {
  const invalidate = useLedgerInvalidation();
  return useMutation({
    mutationFn: async ({ id, reason }: { id: number; reason?: string }) =>
      (await api.post<{ reversalId: number }>(`/transactions/${id}/void`, { reason })).data,
    onSuccess: invalidate,
  });
}

export function useCreateLoan() {
  const invalidate = useLedgerInvalidation();
  return useMutation({
    mutationFn: async (body: Record<string, unknown>) => (await api.post<Loan>('/loans', body)).data,
    onSuccess: invalidate,
  });
}

export function useRepayLoan() {
  const invalidate = useLedgerInvalidation();
  return useMutation({
    mutationFn: async ({ id, ...body }: { id: number } & Record<string, unknown>) =>
      (await api.post<Loan>(`/loans/${id}/repay`, body)).data,
    onSuccess: invalidate,
  });
}

// Reference-data writes touch balances too (renaming a fund changes labels
// everywhere), so they use the same invalidation.

export function useSaveFund() {
  const invalidate = useLedgerInvalidation();
  return useMutation({
    mutationFn: async ({ id, ...body }: { id?: number } & Record<string, unknown>) =>
      id
        ? (await api.patch<Fund>(`/funds/${id}`, body)).data
        : (await api.post<Fund>('/funds', body)).data,
    onSuccess: invalidate,
  });
}

export function useArchiveFund() {
  const invalidate = useLedgerInvalidation();
  return useMutation({
    mutationFn: async ({ id, archived }: { id: number; archived: boolean }) =>
      (await api.post<Fund>(`/funds/${id}/archive`, { archived })).data,
    onSuccess: invalidate,
  });
}

export function useSaveLocation() {
  const invalidate = useLedgerInvalidation();
  return useMutation({
    mutationFn: async ({ id, ...body }: { id?: number } & Record<string, unknown>) =>
      id
        ? (await api.patch<Location>(`/locations/${id}`, body)).data
        : (await api.post<Location>('/locations', body)).data,
    onSuccess: invalidate,
  });
}

export function useArchiveLocation() {
  const invalidate = useLedgerInvalidation();
  return useMutation({
    mutationFn: async ({ id, archived }: { id: number; archived: boolean }) =>
      (await api.post<Location>(`/locations/${id}/archive`, { archived })).data,
    onSuccess: invalidate,
  });
}

export function useSaveCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...body }: { id?: number } & Record<string, unknown>) =>
      id
        ? (await api.patch<Category>(`/categories/${id}`, body)).data
        : (await api.post<Category>('/categories', body)).data,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['categories'] });
      void qc.invalidateQueries({ queryKey: ['reports'] });
    },
  });
}

export function useCreateCounterparty() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: { name: string }) =>
      (await api.post<Counterparty>('/counterparties', body)).data,
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['counterparties'] }),
  });
}

export function useSaveSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: Partial<AppSettings>) =>
      (await api.put<AppSettings>('/settings', body)).data,
    // Changing the base currency re-denominates every displayed figure.
    onSuccess: () => void qc.invalidateQueries(),
  });
}

export function useFetchFxRates() {
  const qc = useQueryClient();
  return useMutation({
    // Explicitly typed: a default parameter would make TanStack infer
    // TVariables as void and reject `mutate(false)`.
    mutationFn: async (historical: boolean) =>
      (await api.post<{ days: number; updated: string[]; skipped: string[] }>('/fx/fetch', { historical })).data,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['fx'] });
      void qc.invalidateQueries({ queryKey: ['balances'] });
      void qc.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });
}

export function useSetManualRate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: { quote: string; date: string; rate: string }) =>
      (await api.post('/fx/rates', body)).data,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['fx'] });
      void qc.invalidateQueries({ queryKey: ['balances'] });
      void qc.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });
}

export function useChangePassword() {
  return useMutation({
    mutationFn: async (body: { currentPassword: string; newPassword: string }) =>
      (await api.post('/auth/change-password', body)).data,
  });
}

// ───────────────────────────── planning ─────────────────────────────
//
// Reads are kept apart from the ledger keys on purpose: a commitment is a
// plan, not money that moved. Confirming an instalment DOES touch the ledger,
// so that mutation invalidates both sets.

const PLAN_KEYS = ['commitments', 'plan'];

function usePlanInvalidation() {
  const qc = useQueryClient();
  return () => {
    for (const key of PLAN_KEYS) void qc.invalidateQueries({ queryKey: [key] });
  };
}

export function useCommitments(params?: {
  includeArchived?: boolean;
  direction?: 'INCOME' | 'EXPENSE';
}) {
  return useQuery({
    queryKey: ['commitments', params ?? null],
    queryFn: async () =>
      (await api.get<{ items: Commitment[] }>('/commitments', { params })).data.items,
  });
}

export function useInstalments(commitmentId: number | null) {
  return useQuery({
    queryKey: ['commitments', commitmentId, 'instalments'],
    queryFn: async () =>
      (await api.get<{ items: Instalment[] }>(`/commitments/${commitmentId}/instalments`)).data
        .items,
    enabled: commitmentId != null,
  });
}

export function useForecast(months = 12) {
  return useQuery({
    queryKey: ['plan', 'forecast', months],
    queryFn: async () => (await api.get<Forecast>('/plan/forecast', { params: { months } })).data,
  });
}

export function useUpcoming(days = 30) {
  return useQuery({
    queryKey: ['plan', 'upcoming', days],
    queryFn: async () =>
      (await api.get<UpcomingResponse>('/plan/upcoming', { params: { days } })).data,
  });
}

export function usePlanCalendar(month: string) {
  return useQuery({
    queryKey: ['plan', 'calendar', month],
    queryFn: async () =>
      (await api.get<CalendarResponse>('/plan/calendar', { params: { month } })).data,
  });
}

export function useSaveCommitment() {
  const invalidatePlan = usePlanInvalidation();
  return useMutation({
    mutationFn: async ({ id, ...body }: { id?: number } & Record<string, unknown>) =>
      id
        ? (await api.patch<Commitment>(`/commitments/${id}`, body)).data
        : (await api.post<Commitment>('/commitments', body)).data,
    onSuccess: invalidatePlan,
  });
}

/** Confirming a payment can move real money, so both worlds are invalidated. */
export function usePayInstalment() {
  const invalidateLedger = useLedgerInvalidation();
  const invalidatePlan = usePlanInvalidation();
  return useMutation({
    mutationFn: async ({ id, ...body }: { id: number } & Record<string, unknown>) =>
      (
        await api.post<{ transactionId: number | null; wroteToLedger: boolean }>(
          `/plan/instalments/${id}/pay`,
          body,
        )
      ).data,
    onSuccess: () => {
      invalidatePlan();
      invalidateLedger();
    },
  });
}

export function useSkipInstalment() {
  const invalidatePlan = usePlanInvalidation();
  return useMutation({
    mutationFn: async ({ id, note }: { id: number; note?: string }) =>
      (await api.post(`/plan/instalments/${id}/skip`, { note })).data,
    onSuccess: invalidatePlan,
  });
}
